import { Box, Button, Flex, HStack, Icon, Text, useDisclosure } from '@televet/kibble-ui';
import { useTracking } from 'shared/providers/TrackingProvider';
import React, { useEffect, useState } from 'react';
import LoadingIndicator from 'shared/components/LoadingIndicator';
import { LoyaltyEvents } from 'shared/providers/TrackingProvider/EventNames';
import { useCustomToast } from 'shared/hooks/useCustomToast';
import {
  LoyaltyProgramStatus,
  useFindOrCreateLoyaltyAccountForClinicPetParentMutation,
  useSubscribeToLoyaltyAccountSubscription,
  useSubscribeToLoyaltyProgramUpdateSubscription,
} from 'shared/types/graphql';
import { useSidePanelSearch } from '../../state/providers/SearchPanelProvider';
import { AddPointsModal } from './components/AddPointsModal';
import { RewardCard } from './components/RewardCard';
import { RewardsListModal } from './components/RewardsListModal';
import { RewardsTab } from './components/RewardsPanel';

interface SidePanelLoyaltyProgramProps {
  petParentName: string;
  petParentId: string;
}

export const SidePanelLoyaltyProgram = ({ petParentName, petParentId }: SidePanelLoyaltyProgramProps): JSX.Element => {
  const { viewLoyaltyRewards } = useSidePanelSearch();
  const { toastMessagePop } = useCustomToast();
  const { trackEvent } = useTracking();
  const [pointBalance, setPointBalance] = useState(0);
  const [isWaitingForUpdate, setIsWaitingForUpdate] = useState(false);
  const { isOpen: isAddPointsModalOpen, onOpen: openAddPointsModal, onClose: closeAddPointsModal } = useDisclosure();
  const {
    isOpen: isRewardsListModalOpen,
    onOpen: openRewardsListModal,
    onClose: closeRewardsListModal,
  } = useDisclosure();

  const [getAccountData, { data: accountData, loading: loadingAccountData }] =
    useFindOrCreateLoyaltyAccountForClinicPetParentMutation({
      variables: { clinicPetParentId: petParentId },
      onCompleted: (data) => {
        if (data.findOrCreateLoyaltyAccountForClinicPetParent) {
          setPointBalance(data.findOrCreateLoyaltyAccountForClinicPetParent.pointBalance);
        }
      },
      onError: (error) => {
        console.error(error);
      },
    });

  useEffect(() => {
    if (petParentId) {
      getAccountData();
    }
  }, [getAccountData, petParentId]);

  const account = accountData?.findOrCreateLoyaltyAccountForClinicPetParent;
  const latestRedemption = account?.rewardRedemptions[0];

  const triggerRefetch = (): void => {
    getAccountData();
  };

  useSubscribeToLoyaltyAccountSubscription({
    variables: {
      where: {
        id: account?.id,
      },
    },
    skip: !account?.id,
    onData: ({ data }) => {
      if (data.data?.loyaltyAccountUpdated.mutation === 'update') {
        toastMessagePop('Points Added!');
        setPointBalance(data.data.loyaltyAccountUpdated.node.pointBalance);
      }
      setIsWaitingForUpdate(false);
      closeAddPointsModal();
    },
  });

  useSubscribeToLoyaltyProgramUpdateSubscription({
    variables: {
      where: {
        id: account?.program.id,
      },
    },
    skip: !account?.program.id,
    onData: ({ data }) => {
      if (data.data?.loyaltyProgramUpdated.mutation === 'update') {
        getAccountData();
      }
    },
  });

  const isLoyaltyProgramActiveForFlow =
    account?.program.status === LoyaltyProgramStatus.Live || account?.program.status === LoyaltyProgramStatus.Test;

  if (!accountData || !isLoyaltyProgramActiveForFlow) {
    return <></>;
  }

  return (
    <Box bgColor="background.subtle" py={5} px={6}>
      <Flex alignItems="center" justifyContent="space-between" w="100%" mb={5}>
        <Text fontWeight="semibold">Loyalty Rewards</Text>
        <Text
          variant="interactive"
          size="xs"
          fontWeight="semibold"
          cursor="pointer"
          onClick={(): void => {
            trackEvent(LoyaltyEvents.SidepanelViewRewardsList);
            openRewardsListModal();
          }}
        >
          View Rewards List
        </Text>
        {isRewardsListModalOpen && account && (
          <RewardsListModal
            isOpen={isRewardsListModalOpen}
            onClose={closeRewardsListModal}
            programId={account.program.id}
          />
        )}
      </Flex>
      {loadingAccountData ? (
        <LoadingIndicator />
      ) : (
        <>
          <Flex
            justifyContent="space-between"
            bgColor="background.warning"
            borderRadius="base"
            py={2}
            px={3}
            w="100%"
            mb={5}
          >
            <Flex alignItems="center">
              <Text fontWeight="semibold" size="sm">
                Treats Balance:
              </Text>
              <Box px="6px" display="inline-flex">
                <Icon variant="warning" name="bone" color="currentColor" size="md" />
              </Box>
              <Text fontWeight="semibold" size="sm">
                {pointBalance}
              </Text>
            </Flex>
            <>
              <Button
                variant="secondarySubtle"
                size="xs"
                onClick={(): void => {
                  trackEvent(LoyaltyEvents.SidepanelAddPointsClick);
                  openAddPointsModal();
                }}
              >
                Add Points
              </Button>
              {account && isAddPointsModalOpen && (
                <AddPointsModal
                  isOpen={isAddPointsModalOpen}
                  onClose={closeAddPointsModal}
                  petParentName={petParentName}
                  accountId={account.id}
                  isWaitingForUpdate={isWaitingForUpdate}
                  setIsWaitingForUpdate={setIsWaitingForUpdate}
                />
              )}
            </>
          </Flex>
          <Box mb={3}>
            <Text as="p" fontWeight="semibold" mb={3}>
              Unfulfilled Rewards
            </Text>
            {!!latestRedemption ? (
              <RewardCard
                reward={latestRedemption.reward}
                rewardRedemptionId={latestRedemption.id}
                requestedDate={latestRedemption.createdAt}
                showButton
                accountId={account?.id || ''}
                triggerRefetch={triggerRefetch}
              />
            ) : (
              <Text size="sm" variant="subtle" as="p" textAlign="center">
                There are no unfulfilled rewards
              </Text>
            )}
          </Box>
        </>
      )}
      <HStack w="100%" spacing={3}>
        <Button
          variant="tertiary"
          size="sm"
          isFullWidth
          onClick={(): void => {
            trackEvent(LoyaltyEvents.SidepanelRewardsHistory, {
              source: 'button',
            });
            viewLoyaltyRewards(RewardsTab.RewardsHistory);
          }}
        >
          Reward History
        </Button>
        <Button
          variant="tertiary"
          size="sm"
          isFullWidth
          onClick={(): void => {
            trackEvent(LoyaltyEvents.SidepanelCurrentRewards, {
              source: 'button',
            });
            viewLoyaltyRewards(RewardsTab.CurrentRewards);
          }}
        >
          Current Rewards
        </Button>
      </HStack>
    </Box>
  );
};
